import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { AuthContext } from "../../../../contexts/AuthContext";
import { departmentService } from "../../../../services/api/departmentService";
import { employeeService } from "../../../../services/api/employeeService";
import { adminService } from "../../../../services/api/adminService";
import { firebaseService } from "../../../../services/api/firebaseService";
import { roleService } from "../../../../services/api/roleService";
import {
  formatDateInput,
  formatDateToDDMMYYYY,
} from "../../../../utils/DateFormat";
import Loading from "../../components/Loading";
import EmployeeDetailForm from "./EmployeeDetailForm";

const RESPONSIBLE_DEPARTMENT_HIDDEN_ROLES = [
  "nhan vien (cms)",
  "nhan vien ( cms )",
  "staff (cms)",
  "staff ( cms )",
  "quan ly phong ban",
  "ql phong ban",
];

const normalizeRoleLabel = (roleLabel = "") =>
  String(roleLabel)
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/đ/g, "d")
    .replace(/Đ/g, "D")
    .trim()
    .toLowerCase();

const shouldHideResponsibleDepartmentsByRole = (roleLabel = "") =>
  RESPONSIBLE_DEPARTMENT_HIDDEN_ROLES.includes(normalizeRoleLabel(roleLabel));

const EmployeeDetail = ({
  mode = "view",
  employeeId,
  departmentId: departmentContextId,
  onSuccess,
  onCancel,
  layout = "popup", // "popup" | "page"
  title,
  isOpen,
  onBack,
  onEditClick,
}) => {
  const { token } = useContext(AuthContext);
  const fileInputRef = useRef(null);
  const permissionTabRef = useRef(null);

  const canEdit = mode === "edit" || mode === "create";

  const [loading, setLoading] = useState(false);
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [activeTab, setActiveTab] = useState("info"); // "info" | "permission"
  const [uploading, setUploading] = useState(false);

  const [data, setData] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [roles, setRoles] = useState([]);
  const [isFetchingEmployeeDetail, setIsFetchingEmployeeDetail] = useState(false);

  const [loadingRoles, setLoadingRoles] = useState(false);
  const [hasLoadedRoles, setHasLoadedRoles] = useState(false);

  const [availableEmployees, setAvailableEmployees] = useState([]);
  const [selectedExistingEmployee, setSelectedExistingEmployee] =
    useState(null);
  const [isAddingExisting, setIsAddingExisting] = useState(false);

  const [imageFile, setImageFile] = useState(null);
  const [imageSrc, setImageSrc] = useState("/images/avatar-default.png");

  const [hasLoadedEmployees, setHasLoadedEmployees] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [pageEmployees, setPageEmployees] = useState(1);
  const [hasMoreEmployees, setHasMoreEmployees] = useState(true);
  const [loadingMoreEmployees, setLoadingMoreEmployees] = useState(false);

  const fetchRoles = async () => {
    if (hasLoadedRoles || loadingRoles) return;
    try {
      setLoadingRoles(true);
      const roleRes = await roleService.get_list_roles(token, {
        page: 1,
        limit: 100,
        type: "CMS",
      });

      const fetchedRoles = roleRes.data?.data || roleRes.data || [];
      setRoles(fetchedRoles);
      setHasLoadedRoles(true);

      if (mode === "create" && !isAddingExisting) {
        const defaultRole = fetchedRoles.find(
          (r) => r.displayName === "Nhân viên" || r.name === "Staff",
        );
        if (defaultRole) {
          setData((prev) => {
            if (!prev || prev?.roleId) return prev;
            return { ...prev, roleId: defaultRole._id };
          });
        }
      }
    } catch (err) {
      console.error("Fetch roles error:", err);
      toast.error(
        err?.response?.data?.message || "Không thể tải danh sách chức vụ",
      );
    } finally {
      setLoadingRoles(false);
    }
  };

  const fetchAvailableEmployees = async (query = "", isLoadMore = false) => {
    try {
      if (isLoadMore) {
        setLoadingMoreEmployees(true);
      } else {
        setSearchLoading(true);
        setPageEmployees(1);
      }

      const currentPage = isLoadMore ? pageEmployees + 1 : 1;
      const limit = 7;

      const res = await adminService.get_all_staff(
        query || null,
        currentPage,
        limit,
        token,
      );

      const newBatch = (res.data || []).filter(
        (emp) =>
          emp.departmentId !== departmentContextId &&
          emp.department?._id !== departmentContextId &&
          emp.department?.id !== departmentContextId,
      );

      setAvailableEmployees((prev) =>
        isLoadMore ? [...prev, ...newBatch] : newBatch,
      );
      setHasMoreEmployees(res.data?.length === limit);
      if (isLoadMore) setPageEmployees(currentPage);
      setHasLoadedEmployees(true);
    } catch (err) {
      console.error("Failed to fetch available employees:", err);
      toast.error("Không thể tải danh sách nhân viên");
    } finally {
      setSearchLoading(false);
      setLoadingMoreEmployees(false);
    }
  };

  const loadMoreEmployees = () => {
    if (!loadingMoreEmployees && hasMoreEmployees) {
      fetchAvailableEmployees(searchQuery, true);
    }
  };

  useEffect(() => {
    if (!isAddingExisting || !hasLoadedEmployees) return;

    const timer = setTimeout(() => {
      fetchAvailableEmployees(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [searchQuery]);

  const handleSelectExistingEmployee = async (id) => {
    if (!id) {
      setSelectedExistingEmployee(null);
      setIsAddingExisting(false);
      setData({
        fullName: "",
        dateOfBirth: null,
        dateOfBirthStr: "",
        gender: "Nam",
        email: "",
        phone: "",
        staffCode: "",
        password: "",
        roleId: "",
        departmentId: departmentContextId || "",
        status: "Đang hoạt động",
        description: "",
        avatarUrl: "",
      });
      setImageSrc("/images/avatar-default.png");
      setImageFile(null);
      return;
    }

    try {
      setIsFetchingEmployeeDetail(true);
      const res = await employeeService.get_detail_employee(token, id);
      const emp = res.data;

      setSelectedExistingEmployee(id);
      setIsAddingExisting(true);

      setData({
        fullName: emp.fullName || emp.account?.fullName || "",
        dateOfBirth: emp.dateOfBirth ? new Date(emp.dateOfBirth) : null,
        dateOfBirthStr: emp.dateOfBirth ? formatDateInput(emp.dateOfBirth) : "",
        gender: emp.gender || "Nam",
        email: emp.email || emp.account?.email || "",
        phone: emp.phone || "",
        staffCode: emp.staffCode || "",
        password: "",
        roleId: emp.role?._id || emp.account?.role?._id || "",
        roleLabel: emp.role?.displayName || emp.role?.name || emp.account?.role?.displayName || "",
        departmentId: departmentContextId,
        responsibleDepartments: (emp.responsibleDepartments || []).map((d) =>
          typeof d === "string" ? d : d._id || d.id,
        ),
        status: emp.status || "Đang hoạt động",
        description: "",
        avatarUrl: emp.avatarUrl || emp.avatar || "",
      });

      setImageSrc(emp.avatarUrl || emp.avatar || "/images/avatar-default.png");
      setImageFile(null);
    } catch (err) {
      toast.error("Không thể tải thông tin nhân viên");
      console.error(err);
    } finally {
      setIsFetchingEmployeeDetail(false);
    }
  };

  useEffect(() => {
    if (!token) return;

    if (mode === "create") {
      setData({
        fullName: "",
        dateOfBirth: null,
        dateOfBirthStr: "",
        gender: "Nam",
        email: "",
        phone: "",
        staffCode: "",
        password: "",
        roleId: "",
        departmentId: departmentContextId || "",
        responsibleDepartments: [],
        status: "Đang hoạt động",
        description: "",
        avatarUrl: "",
      });

      setImageFile(null);
      setImageSrc("/images/avatar-default.png");
      setSelectedExistingEmployee(null);
      setIsAddingExisting(false);
      setHasLoadedEmployees(false);
      setRoles([]);
      setHasLoadedRoles(false);
      return;
    }

    if (!employeeId) return;

    const fetchEmployeeDetail = async () => {
      try {
        setLoading(true);
        const res = await employeeService.get_detail_employee(
          token,
          employeeId,
        );
        const emp = res.data;

        setData({
          fullName: emp.fullName || "",
          dateOfBirth: emp.dateOfBirth ? new Date(emp.dateOfBirth) : null,
          dateOfBirthStr: emp.dateOfBirth
            ? formatDateInput(emp.dateOfBirth)
            : "",
          gender: emp.gender || "Nam",
          email: emp.email || "",
          phone: emp.phone || "",
          staffCode: emp.staffCode || "",
          password: "",
          roleId: emp.role?._id || "",
          roleLabel: emp.role?.displayName || emp.role?.name || "",
          departmentId:
            emp.department?.id || emp.department?._id || emp?._id || "",
          responsibleDepartments: (emp.responsibleDepartments || []).map((d) =>
            typeof d === "string" ? d : d._id || d.id,
          ),
          status: emp.status || "Đang hoạt động",
          description: "",
          avatarUrl: emp.avatar || "",
        });

        setImageSrc(emp.avatar || "/images/avatar-default.png");
        setImageFile(null);
      } catch (err) {
        toast.error("Không thể tải chi tiết nhân viên");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchEmployeeDetail();
  }, [mode, employeeId, departmentContextId, token]);

  useEffect(() => {
    if (!token) return;

    const fetchMetaData = async () => {
      try {
        setLoadingMeta(true);

        const deptRes = await departmentService.get_list_department(token, {
          page: 1,
          limit: 100,
        });

        setDepartments(deptRes.data?.departments || []);
      } catch (err) {
        console.error("Fetch metadata error:", err);
        toast.error(
          err?.response?.data?.message || "Không thể tải danh sách phòng ban",
        );
      } finally {
        setLoadingMeta(false);
      }
    };

    fetchMetaData();
  }, [token]);

  const departmentOptions = useMemo(
    () =>
      (departments || []).map((d) => ({
        value: d._id,
        label: d.name,
      })),
    [departments],
  );

  const roleOptions = useMemo(
    () =>
      (roles || []).map((r) => ({
        value: r._id,
        label: r.displayName || r.name,
      })),
    [roles],
  );

  const employeeOptions = useMemo(
    () => [
      { value: "", label: "-- Hoặc chọn nhân viên có sẵn --" },
      ...availableEmployees.map((emp) => {
        const fullName = emp.account?.fullName || emp.fullName || "N/A";
        const email = emp.account?.email || emp.email || "N/A";
        const staffCode = emp.staffCode || "N/A";

        return {
          value: emp._id,
          label: `${staffCode} - ${fullName} (${email})`,
        };
      }),
    ],
    [availableEmployees],
  );

  const onChange = (key, value) => {
    setData((prev) => ({ ...prev, [key]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!["image/jpeg", "image/png"].includes(file.type)) {
      toast.error("Chỉ chấp nhận định dạng JPEG/PNG.");
      return;
    }

    if (file.size > 25 * 1024 * 1024) {
      toast.error("Kích thước tệp vượt quá 25MB.");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setImageSrc(reader.result || "/images/avatar-default.png");
    };
    reader.onerror = () => {
      toast.error("Không thể đọc tệp. Vui lòng thử lại.");
    };
    reader.readAsDataURL(file);

    setImageFile(file);
  };

  const handleSubmit = async () => {
    if (!canEdit) return;
    if (!data) return;

    if (!data.fullName || !data.email || !data.roleId) {
      toast.error("Vui lòng nhập đầy đủ thông tin bắt buộc");
      return;
    }

    try {
      setUploading(true);

      if (isAddingExisting && selectedExistingEmployee) {
        const selectedRole =
          roleOptions.find((o) => o.value === data.roleId) ||
          (data.roleLabel ? { label: data.roleLabel } : null);
        const shouldHideResponsibleDepartments =
          shouldHideResponsibleDepartmentsByRole(selectedRole?.label);

        await onSuccess?.(
          {
            employeeId: selectedExistingEmployee,
            departmentId: data.departmentId || null,
            responsibleDepartments: shouldHideResponsibleDepartments
              ? []
              : data.responsibleDepartments || [],
          },
          true,
        );
        return;
      }

      let avatarUrl = data.avatarUrl;

      if (imageFile) {
        const uploadRes = await firebaseService.upload_file(token, imageFile);

        if (!uploadRes?.url) {
          toast.error("Upload avatar thất bại");
          return;
        }

        avatarUrl = uploadRes.url;

        if (
          mode === "edit" &&
          data.avatarUrl &&
          data.avatarUrl.startsWith("https://")
        ) {
          try {
            await firebaseService.delete_file(token, data.avatarUrl);
          } catch (err) {
            console.warn("Failed to delete old avatar:", err);
          }
        }
      }

      const selectedRole = roleOptions.find((o) => o.value === data.roleId) ||
        (data.roleLabel ? { label: data.roleLabel } : null);

      const shouldHideResponsibleDepartments =
        shouldHideResponsibleDepartmentsByRole(selectedRole?.label);

      const payload = {
        fullName: data.fullName,
        dateOfBirth: data.dateOfBirth
          ? formatDateToDDMMYYYY(data.dateOfBirth)
          : null,
        gender: data.gender,
        email: data.email,
        phone: data.phone,
        staffCode: mode === "create" ? undefined : data.staffCode,
        roleId: data.roleId,
        departmentId: data.departmentId || null,
        responsibleDepartments: shouldHideResponsibleDepartments
          ? []
          : data.responsibleDepartments || [],
        status: data.status,
        avatarUrl,
      };

      if (data.password?.trim()) {
        payload.password = data.password;
      }

      await onSuccess?.(payload, false);
    } catch (err) {
      toast.error(
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        "Xảy ra lỗi.",
      );
    } finally {
      setUploading(false);
    }
  };

  if (loading || loadingMeta) {
    return (
      <div className="h-[400px] flex items-center justify-center">
        <Loading />
      </div>
    );
  }

  return (
    <EmployeeDetailForm
      mode={mode}
      data={data}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      uploading={uploading}
      loading={loading}
      loadingMeta={loadingMeta}
      isFetchingEmployeeDetail={isFetchingEmployeeDetail}
      isAddingExisting={isAddingExisting}
      setIsAddingExisting={setIsAddingExisting}
      selectedExistingEmployee={selectedExistingEmployee}
      imageSrc={imageSrc}
      searchQuery={searchQuery}
      searchLoading={searchLoading}
      hasLoadedEmployees={hasLoadedEmployees}
      hasMoreEmployees={hasMoreEmployees}
      loadingMoreEmployees={loadingMoreEmployees}
      roleOptions={roleOptions}
      departmentOptions={departmentOptions}
      employeeOptions={employeeOptions}
      loadingRoles={loadingRoles}
      onCancel={onCancel}
      onBack={onBack}
      onEditClick={onEditClick}
      onChange={onChange}
      onImageChange={handleImageChange}
      onSelectExistingEmployee={handleSelectExistingEmployee}
      onSearchQueryChange={setSearchQuery}
      onDropdownOpenEmployees={() => {
        if (!hasLoadedEmployees) {
          fetchAvailableEmployees();
        }
      }}
      onDropdownOpenRoles={fetchRoles}
      onLoadMoreEmployees={loadMoreEmployees}
      onSubmit={handleSubmit}
      onSavePermission={() => permissionTabRef.current?.handleSave()}
      fileInputRef={fileInputRef}
      permissionTabRef={permissionTabRef}
      title={title}
      layout={layout}
      isOpen={isOpen}
      employeeId={employeeId}
      departmentContextId={departmentContextId}
      canEdit={canEdit}
    />
  );
};

export default EmployeeDetail;
